import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Trash2 } from 'lucide-react';
import { projectsAPI, showConfirmModal } from '../services/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { readResponseJSON } from '../utils/fetchSafe';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtCurrency = (val) => {
  const n = parseFloat(val) || 0;
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const ProjectCostOverview = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(100);
  const [costData, setCostData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/commissions`)
      .then(readResponseJSON)
      .then(data => {
        const comms = data.commissions || [];
        setCostData(comms.map(c => ({
          projectNum: c.project_id,
          project: c.customer,
          location: c.location,
          productionManager: c.production_manager || '',
          dateFinished: c.finished_date || 'n/a',
          jobValue: c.subtotal,
          totalExpenses: c.non_mgmt_expenses + c.mgmt_expenses,
          netValue: c.commissionable_total,
          hoursCharged: c.hours_charged,
          hourlyLaborRate: c.hourly_labor_rate,
        })));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleDeleteProject = async (e, projectNum) => {
    e.stopPropagation();
    e.preventDefault();
    if (!(await showConfirmModal('Are you sure you want to delete this project?'))) return;
    try {
      await projectsAPI.delete(projectNum);
      setCostData(prev => prev.filter(p => p.projectNum !== projectNum));
      toast.success('Project deleted');
    } catch (error) {
      if (error.response?.status === 401) return;
      const detail = error.response?.data?.detail || 'Failed to delete project';
      toast.error(detail);
    }
  };

  const filtered = costData.filter(item => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return [item.projectNum, item.project, item.productionManager, item.location]
      .some(f => (f || '').toLowerCase().includes(term));
  }).slice(0, entriesPerPage);

  return (
    <Layout>
      <div className="p-8">
        <div className="mb-6">
          <h2 className="text-3xl text-[#5B8DB8] font-light mb-1">Management</h2>
          <h1 className="text-4xl text-[#5B8DB8] font-light">Project Cost Overview</h1>
        </div>

        <div className="bg-white rounded shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Show</span>
              <select value={entriesPerPage} onChange={(e) => setEntriesPerPage(Number(e.target.value))} className="border border-gray-300 rounded px-3 py-1 text-sm">
                <option value={10}>10</option><option value={50}>50</option><option value={100}>100</option>
              </select>
              <span className="text-sm text-gray-600">entries</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Search:</span>
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="border border-gray-300 rounded px-3 py-1 text-sm w-48" data-testid="cost-search" />
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading project cost data...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="cost-table">
                <thead>
                  <tr className="border-b-2">
                    <th className="text-left p-2 text-gray-600 font-normal">Project #</th>
                    <th className="text-left p-2 text-gray-600 font-normal">Project</th>
                    <th className="text-left p-2 text-gray-600 font-normal">Production Manager</th>
                    <th className="text-left p-2 text-gray-600 font-normal">Date Finished</th>
                    <th className="text-right p-2 text-gray-600 font-normal">Job Value</th>
                    <th className="text-right p-2 text-gray-600 font-normal">Expenses</th>
                    <th className="text-right p-2 text-gray-600 font-normal">Net Value</th>
                    <th className="text-right p-2 text-gray-600 font-normal">Hours Charged</th>
                    <th className="text-right p-2 text-gray-600 font-normal">Hourly Labor Rate</th>
                    {user?.is_admin && <th className="p-2"></th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((item, index) => (
                    <tr key={index} className={`border-b hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`} data-testid={`cost-row-${index}`}>
                      <td className="p-2 text-gray-700">{item.projectNum}</td>
                      <td className="p-2">
                        <div className="text-gray-700">{item.project}</div>
                        <div className="text-xs text-gray-400">{item.location}</div>
                      </td>
                      <td className="p-2 text-gray-700">{item.productionManager}</td>
                      <td className="p-2 text-gray-700">{item.dateFinished}</td>
                      <td className="p-2 text-gray-700 text-right">{fmtCurrency(item.jobValue)}</td>
                      <td className="p-2 text-gray-700 text-right">{fmtCurrency(item.totalExpenses)}</td>
                      <td className="p-2 text-gray-700 text-right">{fmtCurrency(item.netValue)}</td>
                      <td className="p-2 text-gray-700 text-right">{item.hoursCharged.toFixed(2)}</td>
                      <td className="p-2 text-gray-700 text-right">{item.hoursCharged > 0 ? fmtCurrency(item.hourlyLaborRate) : '--'}</td>
                      {user?.is_admin && (
                      <td className="p-2">
                        <button onClick={(e) => handleDeleteProject(e, item.projectNum)} className="text-[#DC3545] hover:text-[#C82333]" data-testid={`delete-btn-${index}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 font-medium">
                    <td className="p-2" colSpan={user?.is_admin ? "5" : "4"}>Totals</td>
                    <td className="p-2 text-right">{fmtCurrency(filtered.reduce((s, i) => s + i.jobValue, 0))}</td>
                    <td className="p-2 text-right">{fmtCurrency(filtered.reduce((s, i) => s + i.totalExpenses, 0))}</td>
                    <td className="p-2 text-right">{fmtCurrency(filtered.reduce((s, i) => s + i.netValue, 0))}</td>
                    <td className="p-2 text-right">{filtered.reduce((s, i) => s + i.hoursCharged, 0).toFixed(2)}</td>
                    <td className="p-2 text-right">
                      {(() => {
                        const totalHrs = filtered.reduce((s, i) => s + i.hoursCharged, 0);
                        const totalLabor = filtered.reduce((s, i) => s + (i.hoursCharged * i.hourlyLaborRate), 0);
                        return totalHrs > 0 ? fmtCurrency(totalLabor / totalHrs) : '--';
                      })()}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ProjectCostOverview;
